import React, { useState } from 'react';
import { BotMessageSquare, ChevronDown, ExternalLink, FileCode2, HelpCircle, Newspaper } from 'lucide-react';
import { FilingPanel } from './FilingPanel';

const newsDate = (value) => {
  const date = new Date(value || '');
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * The generated summary, press coverage, Q&A and extraction provenance --
 * everything that sits beside the filed data rather than inside it.
 */
export const FilingSidebar = ({ filing }) => {
  const [openFaq, setOpenFaq] = useState(null);
  const { narrative, news, faqs } = filing;
  const provenance = [
    { label: 'DRHP file', value: filing.sourceDrhp },
    { label: 'RHP file', value: filing.sourceRhp },
    { label: 'Pages read', value: filing.pagesRead ? `${filing.pagesRead} pages` : null },
    { label: 'Extractor', value: filing.extractorVersion ? `v${filing.extractorVersion}` : null },
    { label: 'Extracted on', value: newsDate(filing.extractedOn) },
  ].filter((row) => row.value);

  return (
    <div className="space-y-4 sm:space-y-5">
      {narrative && (
        <FilingPanel
          id="filing-narrative-heading"
          title="Generated Summary"
          icon={BotMessageSquare}
          action={
            <span className="px-2 py-0.5 rounded-md border border-amber-200 bg-amber-50 text-[10px] font-bold text-amber-700 normal-case">
              Not verified
            </span>
          }
          note={`Written by a language model${narrative.model ? ` (${narrative.model})` : ''} from the extracted text, not by an analyst. It has been observed to state the wrong country, currency or financial year. Treat it as a reading aid only.`}
        >
          {narrative.summary && <p className="text-sm text-slate-600 leading-relaxed">{narrative.summary}</p>}
          {narrative.bullets.length > 0 && (
            <ul className={`space-y-2 ${narrative.summary ? 'mt-3 pt-3 border-t border-slate-100' : ''}`}>
              {narrative.bullets.map((bullet) => (
                <li key={bullet} className="flex items-start gap-2 text-xs text-slate-600 leading-relaxed">
                  <span className="mt-1.5 w-1 h-1 rounded-full bg-slate-400 flex-shrink-0" aria-hidden="true" />
                  {bullet}
                </li>
              ))}
            </ul>
          )}
        </FilingPanel>
      )}

      {news.length > 0 && (
        <FilingPanel id="filing-news-heading" title="Press Coverage" icon={Newspaper}>
          <ul className="space-y-3">
            {news.map((item) => (
              <li key={item.url}>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-start gap-2 text-sm font-bold text-slate-800 hover:text-[#008080] leading-snug"
                >
                  <span className="flex-1">{item.title}</span>
                  <ExternalLink className="w-3.5 h-3.5 mt-0.5 text-slate-300 group-hover:text-[#008080] flex-shrink-0" aria-hidden="true" />
                </a>
                <p className="mt-0.5 text-[11px] text-slate-400">
                  {[item.source, newsDate(item.date)].filter(Boolean).join(' • ')}
                </p>
              </li>
            ))}
          </ul>
        </FilingPanel>
      )}

      {faqs.length > 0 && (
        <FilingPanel id="filing-faqs-heading" title="Questions Answered" icon={HelpCircle}>
          <ul className="divide-y divide-slate-100 -my-2">
            {faqs.map((faq) => {
              const open = openFaq === faq.id;
              return (
                <li key={faq.id} className="py-2">
                  <button
                    type="button"
                    aria-expanded={open}
                    onClick={() => setOpenFaq(open ? null : faq.id)}
                    className="w-full flex items-start justify-between gap-2 text-left text-xs font-bold text-slate-800 hover:text-[#008080]"
                  >
                    <span className="flex-1">{faq.question}</span>
                    <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
                  </button>
                  {open && <p className="mt-1.5 text-xs text-slate-600 leading-relaxed">{faq.answer}</p>}
                </li>
              );
            })}
          </ul>
        </FilingPanel>
      )}

      <FilingPanel id="filing-source-heading" title="Source Document" icon={FileCode2}>
        {provenance.length > 0 && (
          <dl className="space-y-2.5 text-xs">
            {provenance.map((row) => (
              <div key={row.label} className="flex items-start justify-between gap-3">
                <dt className="text-slate-500 font-semibold flex-shrink-0">{row.label}</dt>
                <dd className="text-right font-bold text-slate-800 break-all">{row.value}</dd>
              </div>
            ))}
          </dl>
        )}
        <p className={`text-[11px] text-slate-400 leading-relaxed ${provenance.length ? 'mt-3 pt-3 border-t border-slate-100' : ''}`}>
          The filing PDF itself is not published through this site. Download it from the SEBI or exchange website before
          making any investment decision.
        </p>
      </FilingPanel>
    </div>
  );
};

export default FilingSidebar;
